import { type ReactNode } from 'react';
import { legalBySlug } from './legal';

type Href = { href: string; external: boolean } | null;

const INLINE = /\*\*([^*]+)\*\*|\[([^\]]+)\]\(([^)]+)\)|`([^`]+)`/g;

/** Internal research libraries under docs/legal/reference are not published. */
function resolveHref(raw: string): Href {
  if (/^https?:/i.test(raw)) return { href: raw, external: true };
  if (raw.startsWith('mailto:')) return { href: raw, external: false };
  if (raw.startsWith('#')) return { href: raw, external: false };
  if (raw.includes('reference/')) return null;

  const slug = raw.replace(/\.md$/, '').replace(/^\.\//, '');
  if (legalBySlug.has(slug)) return { href: `/policies/${slug}`, external: false };
  return null;
}

function renderInline(text: string, key: string): ReactNode[] {
  const nodes: ReactNode[] = [];
  let last = 0;
  let match: RegExpExecArray | null;
  INLINE.lastIndex = 0;

  while ((match = INLINE.exec(text)) !== null) {
    if (match.index > last) nodes.push(text.slice(last, match.index));
    const id = `${key}-${match.index}`;

    if (match[1] !== undefined) {
      nodes.push(<strong key={id}>{match[1]}</strong>);
    } else if (match[2] !== undefined) {
      const target = resolveHref(match[3]);
      nodes.push(
        target ? (
          <a
            key={id}
            href={target.href}
            {...(target.external ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
          >
            {match[2]}
          </a>
        ) : (
          <span key={id}>{match[2]}</span>
        ),
      );
    } else if (match[4] !== undefined) {
      nodes.push(<code key={id}>{match[4]}</code>);
    }

    last = INLINE.lastIndex;
  }

  if (last < text.length) nodes.push(text.slice(last));
  return nodes;
}

function splitRow(line: string): string[] {
  return line
    .replace(/^\||\|$/g, '')
    .split('|')
    .map((cell) => cell.trim());
}

function slugify(text: string): string {
  return text
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '');
}

/** Internal document-control metadata that must never reach the public site. */
const INTERNAL_SECTIONS =
  /^#{1,6}\s+(review record|document control|change log|revision history|approved\b.*)\s*$/i;
const INTERNAL_BLOCKQUOTE = /^>\s*\*\*(publication status|internal|draft)/i;

/** Minimal block renderer for the constructs used in docs/legal. */
export function Markdown({ source }: { source: string }) {
  const lines = source.split('\n');
  const blocks: ReactNode[] = [];
  let i = 0;
  let sawTitle = false;

  while (i < lines.length) {
    const line = lines[i];

    if (line.trim() === '') {
      i += 1;
      continue;
    }

    // Skip to the next same-or-higher heading so later public sections still render.
    const internal = INTERNAL_SECTIONS.exec(line.trim());
    if (internal) {
      const level = (/^#+/.exec(line) ?? ['#'])[0].length;
      i += 1;
      while (i < lines.length) {
        const next = /^(#{1,6})\s/.exec(lines[i]);
        if (next && next[1].length <= level) break;
        i += 1;
      }
      continue;
    }

    if (INTERNAL_BLOCKQUOTE.test(line)) {
      while (i < lines.length && lines[i].startsWith('>')) i += 1;
      continue;
    }

    const heading = /^(#{1,6})\s+(.*)$/.exec(line);
    if (heading) {
      const level = heading[1].length;
      const text = heading[2].trim();
      i += 1;
      // The page header already shows the document title.
      if (level === 1 && !sawTitle) {
        sawTitle = true;
        continue;
      }
      const Tag = (level === 2 ? 'h2' : level === 3 ? 'h3' : 'h4') as 'h2' | 'h3' | 'h4';
      blocks.push(
        <Tag key={`h-${i}`} id={slugify(text)}>
          {renderInline(text, `h-${i}`)}
        </Tag>,
      );
      continue;
    }

    if (line.startsWith('> ')) {
      const quote: string[] = [];
      while (i < lines.length && lines[i].startsWith('> ')) {
        quote.push(lines[i].slice(2));
        i += 1;
      }
      blocks.push(
        <blockquote key={`q-${i}`}>{renderInline(quote.join(' '), `q-${i}`)}</blockquote>,
      );
      continue;
    }

    if (line.startsWith('|') && lines[i + 1]?.replace(/[\s|:-]/g, '') === '') {
      const head = splitRow(line);
      i += 2;
      const rows: string[][] = [];
      while (i < lines.length && lines[i].startsWith('|')) {
        rows.push(splitRow(lines[i]));
        i += 1;
      }
      blocks.push(
        <div className="prose-table-wrap" key={`t-${i}`}>
          <table>
            <thead>
              <tr>
                {head.map((cell, c) => (
                  <th key={c}>{renderInline(cell, `th-${i}-${c}`)}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((row, r) => (
                <tr key={r}>
                  {row.map((cell, c) => (
                    <td key={c}>{renderInline(cell, `td-${i}-${r}-${c}`)}</td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>,
      );
      continue;
    }

    if (/^[-*]\s+/.test(line)) {
      const items: string[] = [];
      while (i < lines.length && /^[-*]\s+/.test(lines[i])) {
        items.push(lines[i].replace(/^[-*]\s+/, ''));
        i += 1;
      }
      blocks.push(
        <ul key={`ul-${i}`}>
          {items.map((item, n) => (
            <li key={n}>{renderInline(item, `ul-${i}-${n}`)}</li>
          ))}
        </ul>,
      );
      continue;
    }

    if (/^\d+\.\s+/.test(line)) {
      const items: string[] = [];
      while (i < lines.length && /^\d+\.\s+/.test(lines[i])) {
        items.push(lines[i].replace(/^\d+\.\s+/, ''));
        i += 1;
      }
      blocks.push(
        <ol key={`ol-${i}`}>
          {items.map((item, n) => (
            <li key={n}>{renderInline(item, `ol-${i}-${n}`)}</li>
          ))}
        </ol>,
      );
      continue;
    }

    const paragraph: string[] = [];
    while (
      i < lines.length &&
      lines[i].trim() !== '' &&
      !/^(#{1,6}\s|>\s|\||[-*]\s|\d+\.\s)/.test(lines[i])
    ) {
      paragraph.push(lines[i].trim());
      i += 1;
    }
    blocks.push(<p key={`p-${i}`}>{renderInline(paragraph.join(' '), `p-${i}`)}</p>);
  }

  return <div className="prose">{blocks}</div>;
}
